import { useEffect, useState } from 'react';

/**
 * Tracks the inline size of a container element via ResizeObserver.
 *
 * Returns the element's `contentRect.width` (updated on resize). Initializes
 * to `Infinity` so that breakpoint checks (`width <= 500`) are false until
 * the first real measurement — preventing a flash of collapsed items.
 *
 * Unlike scrollWidth/clientWidth comparison (which oscillates with
 * overflow:visible + flex layouts), this reports the container's own width
 * independent of its content. Consumers can compare against fixed pixel
 * breakpoints that mirror CSS `@container` queries.
 */
export function useContainerWidth(
  ref: React.RefObject<HTMLElement | null>,
): number {
  const [width, setWidth] = useState(Infinity);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return width;
}
